import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import IconSubmittals from '@/assets/svgs/submittals-icon.svg?react'
import { NavigationLink } from '@/components/NavigationLink'
import NavigationGroup from '@/components/NavigationGroup'
import useListingTabs from '@/hooks/useListingTabs'
import { LISTING_PATH, listingPathFor } from '@/pages/Submittals/listing-config'
import { useLocation } from '@/lib/router-dom'

/**
 * The submittals sidebar group: one sub-item per data element the consultant can
 * review. Was a tab bar on the listing until CTD-12496.
 *
 * WHY A SEPARATE COMPONENT: it is the SUBMITTALS_PHASE_2 boundary. Navigation
 * renders on every page, so calling `useListingTabs` there would run the data
 * element config chain app-wide with the flag off — and would make Navigation
 * require DataElementsProvider on pages that have no interest in it. Here it
 * mounts only behind the flag.
 *
 * Falls back to `fallback` (the plain Submittals link) when nothing is
 * configured: one data element is not a choice.
 */
// Exact rather than a bare startsWith: one element's id can prefix another's.
const isListingOf = (path, dataElementId) => {
  const base = listingPathFor(dataElementId)
  return path === base || path.startsWith(`${base}/`)
}

const SubmittalsNavGroup = ({ fallback = null }) => {
  const { currentPath, currentState } = useLocation()
  const { tabs, hasConfigTabs } = useListingTabs()

  const onSubmittalsRoute = currentPath.startsWith(LISTING_PATH)

  // Seeded from the route rather than opened by the effect below, so landing
  // straight on a submittals page paints it open once instead of closed-then-open.
  const [isOpen, setIsOpen] = useState(onSubmittalsRoute)

  // Open on arrival, so a redirect cannot leave the active sub-item hidden behind
  // a collapsed group. Never collapses on its own — that is the user's to do.
  useEffect(() => {
    if (onSubmittalsRoute) setIsOpen(true)
  }, [onSubmittalsRoute])

  // Which element the user is working in. Only the listing names it in the path;
  // a detail page belongs to one just as much but says so in router state, if at
  // all. Without this the highlight simply vanishes the moment you open a row —
  // which the single Submittals link never did, since every sub-page sat under
  // its URL.
  const namedByPath = tabs.find(tab => isListingOf(currentPath, tab.dataElementId))?.dataElementId
  const named = namedByPath || currentState?.tabId

  // A detail page opened straight from a link carries neither, until the
  // submittal lands and writes its element back. Holding the last known one keeps
  // the highlight where the user left it rather than dropping it mid-task.
  const [lastKnown, setLastKnown] = useState(named)
  useEffect(() => {
    if (named) setLastKnown(named)
  }, [named])

  const activeDataElementId = named || (onSubmittalsRoute ? lastKnown : null)

  if (!hasConfigTabs) return fallback

  return (
    <NavigationGroup
      icon={<IconSubmittals />}
      label="Submittals"
      open={isOpen}
      onToggle={() => setIsOpen(!isOpen)}
      active={onSubmittalsRoute}
    >
      {tabs.map(({ dataElementId, label }) => (
        <NavigationLink
          key={dataElementId}
          to={listingPathFor(dataElementId)}
          text={label}
          // Said explicitly rather than left to the path, which cannot answer it
          // on a detail page and would light several sub-items at once besides.
          active={dataElementId === activeDataElementId}
        />
      ))}
    </NavigationGroup>
  )
}

SubmittalsNavGroup.propTypes = {
  fallback: PropTypes.node,
}

export default SubmittalsNavGroup
